// DEPLOY: /var/www/woody.wtf/frontend/src/pages/TradingOptions.js
// Trading > Options (/trading/options). Options chain for US (SMART) and ASX underlyings:
// pick a share, a trading class (when IBKR lists more than one) and an expiry; the chain shows
// calls | strike | puts for the strikes nearest the share price - bid, ask, last, volume, open
// interest, implied volatility and delta, with in-the-money shading and the share price marked.
// Click a bid to sell or an ask to buy: the option ticket shows the greeks, premium, breakeven,
// max profit / loss and a payoff-at-expiry chart, with Preview (IBKR what-if) and Place.
// Other order types for an option: "More order types" opens it on the Trade page.
// Strategy builder mode: presets (spreads, straddles, strangles, iron condor, butterfly, covered
// call, protective put, collar) or legs clicked from the chain; shows the combined payoff at expiry,
// max profit / loss, breakevens, chance of profit and net greeks. Two or more option legs go to
// IBKR as one combo order at a single net price (/combo); a share leg goes as its own limit order.
// Data: /api/trading/<broker>/options/params and /options/chain (trading.py -> ibkr_bridge.py).
// Quotes and greeks need market data subscriptions for both the option and the share; without
// them IBKR returns delayed values or blanks. The chain reloads every 20 seconds.
// Admin-only for now (useTradingAccount guard; API is @admin_required in trading.py).
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import TradingTabs from '../components/TradingTabs';
import api from '../api';
import {
  s, isNum, num, signed, tone, arrow, qty, price, errText,
  useTradingAccount, StatusBar, SymbolSearch,
} from '../components/tradingShared';

const CHAIN_REFRESH_MS = 20000;
const STRIKE_COUNTS = [10, 16, 20, 30];
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const expDate = e => new Date(Number(e.slice(0, 4)), Number(e.slice(4, 6)) - 1, Number(e.slice(6, 8)));
const expLabel = e => `${e.slice(6, 8)} ${MONTHS[Number(e.slice(4, 6)) - 1]} ${e.slice(2, 4)}`;
const dte = e => Math.max(0, Math.ceil((expDate(e) - new Date(new Date().toDateString())) / 86400000));
const pct1 = v => isNum(v) ? `${(Number(v) * 100).toFixed(1)}%` : '—';
const dec = (v, dp) => isNum(v) ? Number(v).toFixed(dp) : '—';
const mid = leg => isNum(leg?.bid) && isNum(leg?.ask) && leg.bid > 0 && leg.ask > 0 ? (leg.bid + leg.ask) / 2 : null;

// ── Payoff at expiry (any position: one option or a strategy) ────────────────
// pnl(S) = profit / loss at expiry if the share ends at S; drawn between lo and hi,
// green where it makes money and red where it loses, with breakevens and today's price marked.
function Payoff({ pnl, lo, hi, und, ccy, breakevens = [], height = 170 }) {
  const [hover, setHover] = useState(null);
  const ref = useRef(null);
  const N = 120, W = 360, H = height, L = 6, R = 6, T = 10, B = 20;
  const xs = Array.from({ length: N + 1 }, (_, i) => lo + (hi - lo) * i / N);
  const ys = xs.map(pnl);
  const yMin = Math.min(...ys, 0), yMax = Math.max(...ys, 0), pad = (yMax - yMin) * 0.1 || 1;
  const X = S => L + (S - lo) / (hi - lo) * (W - L - R);
  const Y = v => T + (yMax + pad - v) / (yMax - yMin + 2 * pad) * (H - T - B);
  const move = e => {
    const r = ref.current.getBoundingClientRect();
    setHover(Math.min(N, Math.max(0, Math.round(((e.clientX - r.left) / r.width * W - L) / (W - L - R) * N))));
  };
  return (
    <div>
      <div style={{ ...s.meta, ...s.mono, minHeight: '18px' }}>
        {hover !== null ? `If ${price(xs[hover])} at expiry: ${signed(ys[hover])} ${ccy}` : 'Profit / loss at expiry - hover the chart'}
      </div>
      <svg ref={ref} viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', display: 'block', cursor: 'crosshair' }}
           onMouseMove={move} onMouseLeave={() => setHover(null)}>
        <line x1={L} x2={W - R} y1={Y(0)} y2={Y(0)} style={{ stroke: 'var(--border)' }} strokeWidth="1" />
        {xs.slice(1).map((S, i) => {                     // each step coloured by profit (green) or loss (red)
          const a = ys[i], b = ys[i + 1];
          return <line key={i} x1={X(xs[i])} y1={Y(a)} x2={X(S)} y2={Y(b)} strokeWidth="2" strokeLinecap="round"
                       style={{ stroke: (a + b) / 2 >= 0 ? 'var(--success)' : 'var(--error)' }} />;
        })}
        {und > lo && und < hi && <>
          <line x1={X(und)} x2={X(und)} y1={T} y2={H - B} style={{ stroke: 'var(--text-muted)' }} strokeDasharray="3 3" strokeWidth="1" />
          <text x={X(und) + 3} y={T + 9} fontSize="10" style={{ fill: 'var(--text-muted)' }}>now {price(und)}</text>
        </>}
        {breakevens.filter(be => be > lo && be < hi).map(be => (
          <g key={be}>
            <circle cx={X(be)} cy={Y(0)} r="4" style={{ fill: 'var(--surface)', stroke: 'var(--text)' }} strokeWidth="1.5" />
            <text x={X(be)} y={H - 6} fontSize="10" textAnchor="middle" style={{ fill: 'var(--text-muted)' }}>BE {price(be)}</text>
          </g>
        ))}
        {hover !== null && <circle cx={X(xs[hover])} cy={Y(ys[hover])} r="4" style={{ fill: ys[hover] >= 0 ? 'var(--success)' : 'var(--error)', stroke: 'var(--surface)' }} strokeWidth="2" />}
        <rect x={L} y={T} width={W - L - R} height={H - T - B} fill="transparent" />
      </svg>
    </div>
  );
}

// ── Ticket for one option ────────────────────────────────────────────────────
function OptionTicket({ broker, acc, chain, sel, onClose }) {
  const navigate = useNavigate();
  const row = chain.rows.find(r => r.strike === sel.strike);
  const leg = row?.[sel.right === 'C' ? 'call' : 'put'];
  const [side, setSide]   = useState(sel.side);
  const [n, setN]         = useState('1');
  const [type, setType]   = useState('LIMIT');
  const [px, setPx]       = useState(sel.price ? String(sel.price) : '');
  const [tif, setTif]     = useState('DAY');
  const [preview, setPreview] = useState(null);
  const [busy, setBusy]   = useState('');
  const [msg, setMsg]     = useState('');
  const [err, setErr]     = useState('');

  // A new pick from the chain resets the ticket to it.
  useEffect(() => {
    setSide(sel.side); setPx(sel.price ? String(sel.price) : ''); setPreview(null); setMsg(''); setErr('');
  }, [sel]);

  if (!leg) return null;
  const mult = chain.multiplier || 100, ccy = chain.currency;
  const buy = side === 'BUY', sideSign = buy ? 1 : -1;
  const contracts = Math.floor(Number(n) || 0);
  const prem = Number(type === 'LIMIT' ? px : (buy ? leg.ask : leg.bid) || mid(leg) || leg.last) || 0;
  const total = prem * mult * contracts;
  const be = sel.right === 'C' ? sel.strike + prem : sel.strike - prem;
  // Max profit / loss at expiry for a single leg.
  const unlimited = 'Unlimited';
  const maxProfit = buy ? (sel.right === 'C' ? unlimited : num((sel.strike - prem) * mult * contracts)) : num(total);
  const maxLoss   = buy ? num(total) : (sel.right === 'C' ? unlimited : num((sel.strike - prem) * mult * contracts));
  const ready = contracts > 0 && (type === 'MARKET' || Number(px) > 0) && !busy;

  const body = () => ({ acc_id: acc.acc_id, code: leg.code, side, order_type: type, qty: contracts, price: px, tif });
  const doPreview = async () => {
    setBusy('preview'); setErr(''); setMsg('');
    try { setPreview((await api.post(`/trading/${broker}/whatif`, body())).data.preview); }
    catch (e) { setErr(errText(e)); }
    finally { setBusy(''); }
  };
  const place = async () => {
    const what = `${side} ${contracts} x ${leg.desc} ${type === 'LIMIT' ? `limit ${px}` : 'at market'}, ${tif}\n≈ ${num(total)} ${ccy} premium`;
    if (acc.trd_env === 'REAL' && !window.confirm(`LIVE ORDER with real money on account ${acc.acc_id}:\n\n${what}\n\nPlace it?`)) return;
    setBusy('place'); setErr(''); setMsg('');
    try { setMsg((await api.post(`/trading/${broker}/order`, body())).data.message); setPreview(null); }
    catch (e) { setErr(errText(e)); }
    finally { setBusy(''); }
  };

  const kv = (k, v, color) => (
    <div key={k} style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', fontSize: '12px' }}>
      <span style={{ color: 'var(--text-muted)' }}>{k}</span><span style={{ ...s.mono, color: color || 'var(--text)' }}>{v}</span>
    </div>
  );

  return (
    <div style={{ ...s.card, position: 'sticky', top: '16px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
        <span style={s.title}>{sel.right === 'C' ? 'Call' : 'Put'} option</span>
        <span style={s.badge(acc.trd_env === 'REAL')}>{acc.trd_env === 'REAL' ? 'LIVE' : 'PAPER'}</span>
        <span style={{ ...s.link, marginLeft: 'auto', color: 'var(--text-muted)' }} onClick={onClose}>✕</span>
      </div>
      <div style={{ ...s.mono, fontSize: '14px', fontWeight: 500, marginBottom: '12px' }}>{leg.desc}</div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px', marginBottom: '12px' }}>
        {kv('Bid × size', `${price(leg.bid)} × ${qty(leg.bid_size)}`)}
        {kv('Ask × size', `${price(leg.ask)} × ${qty(leg.ask_size)}`)}
        {kv('Mid', price(mid(leg)))}
        {kv('Last', price(leg.last))}
        {kv('Implied vol', pct1(leg.iv))}
        {kv('Delta', isNum(leg.delta) ? `${(Number(leg.delta) * 100).toFixed(1)} (${dec(leg.delta, 3)})` : '—')}
        {kv('Gamma', dec(leg.gamma, 4))}
        {kv('Theta / day', dec(leg.theta, 4))}
        {kv('Vega', dec(leg.vega, 4))}
        {kv('Open interest', qty(leg.open_interest))}
        {kv('Volume', qty(leg.volume))}
        {kv('Chance ITM ≈', isNum(leg.delta) ? `${Math.round(Math.abs(leg.delta) * 100)}%` : '—')}
      </div>

      <div style={{ display: 'flex', marginBottom: '12px' }}>
        {['BUY', 'SELL'].map((sd, i) => (
          <button key={sd} onClick={() => { setSide(sd); setPreview(null); }}
                  style={{ ...s.seg(side === sd), borderRadius: i ? '0 var(--radius) var(--radius) 0' : 'var(--radius) 0 0 var(--radius)',
                           ...(side === sd ? { background: sd === 'BUY' ? 'var(--success)' : 'var(--error)', borderColor: 'transparent' } : {}) }}>
            {sd === 'BUY' ? 'Buy to open / close' : 'Sell to open / close'}
          </button>
        ))}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '10px' }}>
        <div>
          <div style={s.label}>Contracts</div>
          <input style={s.input} type="number" min="1" step="1" value={n} onChange={e => { setN(e.target.value); setPreview(null); }} />
        </div>
        <div>
          <div style={s.label}>Order type</div>
          <select style={{ ...s.select, width: '100%' }} value={type} onChange={e => { setType(e.target.value); setPreview(null); }}>
            <option value="LIMIT">Limit</option><option value="MARKET">Market</option>
          </select>
        </div>
        <div>
          <div style={s.label}>Limit price</div>
          <input style={s.input} type="number" min="0" step="any" value={type === 'LIMIT' ? px : ''} disabled={type === 'MARKET'}
                 onChange={e => { setPx(e.target.value); setPreview(null); }} />
          {type === 'LIMIT' && (
            <div style={{ display: 'flex', gap: '8px', marginTop: '4px', fontSize: '11px' }}>
              {[['Bid', leg.bid], ['Mid', mid(leg)], ['Ask', leg.ask]].filter(([, v]) => isNum(v) && v > 0).map(([k, v]) => (
                <span key={k} style={s.link} onClick={() => { setPx(String(Number(v.toFixed(2)))); setPreview(null); }}>{k}</span>
              ))}
            </div>
          )}
        </div>
        <div>
          <div style={s.label}>Time in force</div>
          <select style={{ ...s.select, width: '100%' }} value={tif} onChange={e => { setTif(e.target.value); setPreview(null); }}>
            <option value="DAY">Day</option><option value="GTC">Good till cancelled</option>
          </select>
        </div>
      </div>

      <div style={{ padding: '10px 12px', background: 'var(--bg)', borderRadius: 'var(--radius)', marginBottom: '12px' }}>
        {kv(buy ? 'Premium paid' : 'Premium received', `${num(total)} ${ccy}`)}
        {kv('Per contract', `${price(prem)} × ${mult} = ${num(prem * mult)} ${ccy}`)}
        {kv('Breakeven at expiry', price(be))}
        {kv('Max profit', maxProfit === unlimited ? unlimited : `${maxProfit} ${ccy}`, 'var(--success)')}
        {kv('Max loss', maxLoss === unlimited ? unlimited : `${maxLoss} ${ccy}`, 'var(--error)')}
      </div>
      {prem > 0 && contracts > 0 && (() => {
        const centre = chain.underlying_price || sel.strike;
        const intrinsic = S => sel.right === 'C' ? Math.max(0, S - sel.strike) : Math.max(0, sel.strike - S);
        return <Payoff pnl={S => sideSign * (intrinsic(S) - prem) * mult * contracts}
                       lo={Math.min(sel.strike, centre) * 0.75} hi={Math.max(sel.strike, centre) * 1.25}
                       und={chain.underlying_price} ccy={ccy} breakevens={[be]} />;
      })()}

      <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
        <button style={{ ...s.btn, flex: 1 }} onClick={doPreview} disabled={!ready}>{busy === 'preview' ? 'Checking…' : 'Preview'}</button>
        <button onClick={place} disabled={!ready}
                style={{ ...s.btn, flex: 2, border: 'none', fontSize: '13px', fontWeight: 600,
                         background: ready ? (buy ? 'var(--success)' : 'var(--error)') : 'var(--border)',
                         color: ready ? 'var(--on-accent)' : 'var(--text-muted)', cursor: ready ? 'pointer' : 'default' }}>
          {busy === 'place' ? 'Placing…' : `${buy ? 'Buy' : 'Sell'} ${contracts || ''} ${contracts === 1 ? 'contract' : 'contracts'}`}
        </button>
      </div>
      <div style={{ marginTop: '8px', textAlign: 'right' }}>
        <span style={s.link} onClick={() => navigate(`/trading/trade?${new URLSearchParams({ code: leg.code, side, qty: String(contracts || 1) })}`)}>
          More order types ↗</span>
      </div>
      {msg && <div style={s.ok}>{msg}</div>}
      {err && <div style={s.err}>✕ {err}</div>}
      {preview && (
        <div style={{ marginTop: '10px', padding: '10px 12px', background: 'var(--bg)', borderRadius: 'var(--radius)' }}>
          <div style={{ ...s.title, fontSize: '12px', marginBottom: '6px' }}>Preview - nothing placed</div>
          {kv('Commission', isNum(preview.commission) ? `${num(preview.commission)} ${preview.commissionCurrency || ''}`
            : isNum(preview.minCommission) ? `${num(preview.minCommission)} – ${num(preview.maxCommission)} ${preview.commissionCurrency || ''}` : '—')}
          {kv('Initial margin change', num(preview.initMarginChange))}
          {kv('Maintenance margin change', num(preview.maintMarginChange))}
          {kv('Equity with loan after', num(preview.equityWithLoanAfter))}
          {preview.warningText && <div style={{ ...s.err, fontSize: '12px' }}>⚠ {preview.warningText}</div>}
        </div>
      )}
    </div>
  );
}

// ── Strategy maths ───────────────────────────────────────────────────────────
// A leg: {key, kind 'opt'|'stk', code, desc, strike, right, expiry, action BUY|SELL, ratio, price}.
// For a share leg ratio is shares per strategy unit (the contract multiplier, e.g. 100).
const sgn = l => l.action === 'BUY' ? 1 : -1;
const erf = x => {                                  // Abramowitz & Stegun 7.1.26
  const t = 1 / (1 + 0.3275911 * Math.abs(x));
  const y = 1 - (((((1.061405429 * t - 1.453152027) * t) + 1.421413741) * t - 0.284496736) * t + 0.254829592) * t * Math.exp(-x * x);
  return x >= 0 ? y : -y;
};
const normCdf = x => 0.5 * (1 + erf(x / Math.SQRT2));

// P&L at expiry, max profit / loss (unlimited when the far-right slope isn't flat), breakevens,
// cash paid (+) or received (-), and the chance of any profit if the share moves lognormally
// with the legs' average implied volatility.
function analyse(legs, n, mult, und, days, ivs) {
  const pnl = S => legs.reduce((t, l) => {
    if (l.kind === 'stk') return t + sgn(l) * l.ratio * n * (S - l.price);
    const intr = l.right === 'C' ? Math.max(0, S - l.strike) : Math.max(0, l.strike - S);
    return t + sgn(l) * l.ratio * n * mult * (intr - l.price);
  }, 0);
  const ks = legs.filter(l => l.kind === 'opt').map(l => l.strike);
  const top = Math.max(und || 0, ...ks, 1) * 3, G = 1500;
  const xs = Array.from({ length: G + 1 }, (_, i) => top * i / G), ys = xs.map(pnl);
  const far = pnl(top * 10) - pnl(top * 10 - 1);
  // Breakevens: where P&L changes sign, skipping exact zeros (interpolate, or the middle of a zero run).
  const breakevens = [];
  let prev = -1;
  for (let i = 0; i <= G; i++) {
    if (ys[i] === 0) continue;
    if (prev >= 0 && Math.sign(ys[i]) !== Math.sign(ys[prev])) {
      breakevens.push(i - prev === 1 ? xs[prev] + (xs[i] - xs[prev]) * (0 - ys[prev]) / (ys[i] - ys[prev])
                                     : (xs[prev + 1] + xs[i - 1]) / 2);
    }
    prev = i;
  }
  const kinks = [...ks, und || 0].map(pnl);           // payoffs peak / trough at strikes - check them exactly
  let pop = null;
  const iv = ivs.length ? ivs.reduce((a, b) => a + b, 0) / ivs.length : null;
  if (iv && und && days > 0) {
    const sig = iv * Math.sqrt(days / 365), mu = Math.log(und) - sig * sig / 2;
    const cdf = x => x <= 0 ? 0 : normCdf((Math.log(x) - mu) / sig);
    pop = 0;
    for (let i = 1; i <= G; i++) if ((ys[i - 1] + ys[i]) / 2 > 0) pop += cdf(xs[i]) - cdf(xs[i - 1]);
    if (far > 1e-9) pop += 1 - cdf(top);              // profit keeps going above the grid
  }
  const cost = legs.reduce((t, l) => t + sgn(l) * l.ratio * n * l.price * (l.kind === 'opt' ? mult : 1), 0);
  return { pnl, maxProfit: Math.max(...ys, ...kinks), maxLoss: Math.min(...ys, ...kinks), upUnlimited: far > 1e-9, downUnlimited: far < -1e-9,
           breakevens, pop, cost };
}

// Presets: legs as [right 'C'|'P'|'S' (share), strike steps from the money (× width), action, ratio].
const PRESETS = [
  ['bull_call', 'Bull call spread', [['C', 0, 'BUY'], ['C', 1, 'SELL']]],
  ['bear_call', 'Bear call spread', [['C', 0, 'SELL'], ['C', 1, 'BUY']]],
  ['bull_put', 'Bull put spread', [['P', 0, 'SELL'], ['P', -1, 'BUY']]],
  ['bear_put', 'Bear put spread', [['P', 0, 'BUY'], ['P', -1, 'SELL']]],
  ['long_straddle', 'Long straddle', [['C', 0, 'BUY'], ['P', 0, 'BUY']]],
  ['short_straddle', 'Short straddle', [['C', 0, 'SELL'], ['P', 0, 'SELL']]],
  ['long_strangle', 'Long strangle', [['C', 1, 'BUY'], ['P', -1, 'BUY']]],
  ['short_strangle', 'Short strangle', [['C', 1, 'SELL'], ['P', -1, 'SELL']]],
  ['iron_condor', 'Iron condor', [['P', -2, 'BUY'], ['P', -1, 'SELL'], ['C', 1, 'SELL'], ['C', 2, 'BUY']]],
  ['butterfly', 'Long call butterfly', [['C', -1, 'BUY'], ['C', 0, 'SELL', 2], ['C', 1, 'BUY']]],
  ['covered_call', 'Covered call', [['S', 0, 'BUY'], ['C', 1, 'SELL']]],
  ['protective_put', 'Protective put', [['S', 0, 'BUY'], ['P', -1, 'BUY']]],
  ['collar', 'Collar', [['S', 0, 'BUY'], ['P', -1, 'BUY'], ['C', 1, 'SELL']]],
];

const legPrice = (live, action) => mid(live) ?? (action === 'BUY' ? live?.ask : live?.bid) ?? live?.last ?? 0;

function optionLeg(chain, strike, right, action, ratio = 1, px) {
  const row = chain.rows.find(r => r.strike === strike);
  const live = row?.[right === 'C' ? 'call' : 'put'];
  if (!live) return null;
  return { key: `${strike}${right}`, kind: 'opt', code: live.code, desc: live.desc, strike, right, expiry: chain.expiry,
           action, ratio, price: Number((px ?? legPrice(live, action)) || 0) };
}

function buildPreset(id, chain, und, width) {
  const preset = PRESETS.find(p => p[0] === id);
  const rows = chain.rows;
  if (!preset || !rows.length) return { legs: [], missing: 0 };
  let atm = 0;
  rows.forEach((r, i) => { if (Math.abs(r.strike - und) < Math.abs(rows[atm].strike - und)) atm = i; });
  const legs = [];
  let missing = 0;
  preset[2].forEach(([right, step, action, ratio = 1]) => {
    if (right === 'S') {
      legs.push({ key: 'stk', kind: 'stk', code: chain.code, desc: `${chain.code} shares`, action,
                  ratio: chain.multiplier || 100, price: Number(und) || 0 });
      return;
    }
    const i = Math.min(rows.length - 1, Math.max(0, atm + step * width));
    const leg = optionLeg(chain, rows[i].strike, right, action, ratio);
    if (leg && !legs.some(l => l.key === leg.key)) legs.push(leg); else missing += 1;   // no option, or clamped onto a used strike
  });
  return { legs, missing };
}

// ── Strategy builder panel ───────────────────────────────────────────────────
function StrategyPanel({ broker, acc, chain, legs, setLegs }) {
  const [preset, setPreset] = useState('');
  const [width, setWidth]   = useState(1);
  const [n, setN]           = useState('1');
  const [type, setType]     = useState('LIMIT');
  const [tif, setTif]       = useState('DAY');
  const [net, setNet]       = useState('');       // '' = use the legs' prices
  const [preview, setPreview] = useState(null);
  const [busy, setBusy]     = useState('');
  const [msg, setMsg]       = useState('');
  const [err, setErr]       = useState('');
  const [note, setNote]     = useState('');

  const und = chain.underlying_price, mult = chain.multiplier || 100, ccy = chain.currency;
  const units = Math.floor(Number(n) || 0);
  const liveOf = l => l.kind === 'opt' && l.expiry === chain.expiry
    ? chain.rows.find(r => r.strike === l.strike)?.[l.right === 'C' ? 'call' : 'put'] : null;
  const opt = legs.filter(l => l.kind === 'opt'), stk = legs.find(l => l.kind === 'stk');
  const legNet = opt.reduce((t, l) => t + sgn(l) * l.ratio * l.price, 0);         // per unit, per share
  const natural = opt.reduce((t, l) => { const v = liveOf(l); return t + sgn(l) * l.ratio * (Number(l.action === 'BUY' ? v?.ask : v?.bid) || l.price); }, 0);
  const netPx = net === '' ? legNet : Number(net) || 0;
  const days = dte(chain.expiry);
  const ivs = opt.map(l => liveOf(l)?.iv).filter(v => isNum(v) && v > 0).map(Number);
  const a = legs.length ? analyse(legs, units || 1, mult, und, days, ivs) : null;
  const greeks = legs.reduce((g, l) => {
    const k = sgn(l) * l.ratio * (units || 1);
    if (l.kind === 'stk') return { ...g, delta: g.delta + k };
    const v = liveOf(l) || {};
    return { delta: g.delta + k * mult * (Number(v.delta) || 0), gamma: g.gamma + k * mult * (Number(v.gamma) || 0),
             theta: g.theta + k * mult * (Number(v.theta) || 0), vega: g.vega + k * mult * (Number(v.vega) || 0) };
  }, { delta: 0, gamma: 0, theta: 0, vega: 0 });

  const reset = () => { setPreview(null); setMsg(''); setErr(''); };
  const apply = id => {
    setPreset(id); reset(); setNet('');
    if (!id) return;
    const r = buildPreset(id, chain, und, width);
    setLegs(r.legs);
    setNote(r.missing ? `${r.missing} leg${r.missing > 1 ? 's' : ''} had no option at that strike - widen the strikes shown or pick another width.` : '');
  };
  const edit = (key, patch) => { setLegs(ls => ls.map(l => l.key === key ? { ...l, ...patch } : l)); setNet(''); reset(); };
  const remove = key => { setLegs(ls => ls.filter(l => l.key !== key)); setNet(''); reset(); };

  // What goes to IBKR: 2+ option legs as one combo, 1 option leg as a plain order, the share leg as its own order.
  const optionOrder = () => opt.length >= 2
    ? ['combo', { acc_id: acc.acc_id, underlying: chain.code, legs: opt.map(l => ({ code: l.code, action: l.action, ratio: l.ratio })),
                  qty: units, order_type: type, price: netPx, tif }]
    : opt.length === 1
      ? ['order', { acc_id: acc.acc_id, code: opt[0].code, side: opt[0].action, order_type: type, qty: opt[0].ratio * units,
                    price: Math.abs(netPx) / opt[0].ratio, tif }]
      : null;
  const ready = units > 0 && legs.length > 0 && (type === 'MARKET' || opt.length === 0 || netPx !== 0) && !busy;

  const doPreview = async () => {
    const o = optionOrder();
    if (!o) return;
    setBusy('preview'); reset();
    try {
      const r = o[0] === 'combo' ? await api.post(`/trading/${broker}/combo`, { ...o[1], preview: true })
                                 : await api.post(`/trading/${broker}/whatif`, o[1]);
      setPreview(r.data.preview);
    } catch (e) { setErr(errText(e)); }
    finally { setBusy(''); }
  };
  const place = async () => {
    const lines = legs.map(l => `${l.action} ${l.ratio * units} x ${l.desc}${l.kind === 'stk' ? ` (limit ${price(l.price)})` : ''}`);
    const how = opt.length >= 2 ? `as one combo, net ${netPx >= 0 ? 'debit' : 'credit'} ${price(Math.abs(netPx))} per unit` : '';
    if (!window.confirm(`LIVE ORDER with real money on account ${acc.acc_id}:\n\n${lines.join('\n')}\n${how}${stk ? '\nThe share leg goes as its own limit order.' : ''}\n\nPlace it?`)) return;
    setBusy('place'); reset();
    const done = [];
    try {
      const o = optionOrder();
      if (o) done.push((await api.post(`/trading/${broker}/${o[0]}`, o[1])).data.message);
      if (stk) done.push((await api.post(`/trading/${broker}/order`, { acc_id: acc.acc_id, code: chain.code, side: stk.action,
        order_type: 'LIMIT', qty: stk.ratio * units, price: stk.price, tif })).data.message);
      setMsg(done.join(' · '));
    } catch (e) { setErr(`${done.length ? `${done.join(' · ')} - then: ` : ''}${errText(e)}`); }
    finally { setBusy(''); }
  };

  const kv = (k, v, color) => (
    <div key={k} style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', fontSize: '12px' }}>
      <span style={{ color: 'var(--text-muted)' }}>{k}</span><span style={{ ...s.mono, color: color || 'var(--text)' }}>{v}</span>
    </div>
  );
  const small = { ...s.input, padding: '3px 6px', fontSize: '12px' };
  const ks = opt.map(l => l.strike).concat(und ? [und] : []);
  const lo = ks.length ? Math.min(...ks) * 0.8 : 0, hi = ks.length ? Math.max(...ks) * 1.2 : 1;

  return (
    <div style={{ ...s.card, position: 'sticky', top: '16px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
        <span style={s.title}>Strategy builder</span>
        <span style={s.badge(acc.trd_env === 'REAL')}>{acc.trd_env === 'REAL' ? 'LIVE' : 'PAPER'}</span>
        {legs.length > 0 && <span style={{ ...s.link, marginLeft: 'auto', color: 'var(--text-muted)' }} onClick={() => { setLegs([]); setPreset(''); setNet(''); reset(); setNote(''); }}>Clear</span>}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '8px', marginBottom: '8px' }}>
        <select style={s.select} value={preset} onChange={e => apply(e.target.value)}>
          <option value="">Choose a strategy…</option>
          {PRESETS.map(([id, label]) => <option key={id} value={id}>{label}</option>)}
        </select>
        <select style={s.select} value={width} title="How many strikes apart the legs are"
                onChange={e => { const w = Number(e.target.value); setWidth(w); if (preset) { const r = buildPreset(preset, chain, und, w); setLegs(r.legs); setNet(''); reset(); } }}>
          {[1, 2, 3, 4, 5].map(w => <option key={w} value={w}>{w} strike{w > 1 ? 's' : ''} wide</option>)}
        </select>
      </div>
      <div style={{ ...s.meta, marginBottom: '10px' }}>Or click bids (sell) and asks (buy) in the chain to add legs.</div>
      {note && <div style={{ ...s.err, fontSize: '12px', marginTop: 0, marginBottom: '8px' }}>{note}</div>}

      {legs.length === 0 ? <div style={{ ...s.meta, padding: '20px 0', textAlign: 'center' }}>No legs yet.</div> : (
        <>
          <table style={{ ...s.table, marginBottom: '10px' }}>
            <thead><tr>{['', 'Qty', 'Leg', 'Bid / ask', 'Price', ''].map((h, i) => <th key={i} style={{ ...s.th, padding: '4px 6px' }}>{h}</th>)}</tr></thead>
            <tbody>{legs.map(l => {
              const v = liveOf(l);
              return (
                <tr key={l.key}>
                  <td style={{ ...s.td, padding: '4px 6px' }}>
                    <span style={{ ...s.pill(l.action === 'BUY' ? 'var(--success-dim)' : 'var(--error-dim)', l.action === 'BUY' ? 'var(--success)' : 'var(--error)'), cursor: 'pointer' }}
                          title="Switch buy / sell" onClick={() => edit(l.key, { action: l.action === 'BUY' ? 'SELL' : 'BUY' })}>{l.action}</span>
                  </td>
                  <td style={{ ...s.td, padding: '4px 6px', width: '56px' }}>
                    <input style={small} type="number" min="1" step="1" value={l.ratio} onChange={e => edit(l.key, { ratio: Math.max(1, Math.floor(Number(e.target.value) || 1)) })} />
                  </td>
                  <td style={{ ...s.td, padding: '4px 6px', fontSize: '11px' }}>{l.kind === 'stk' ? 'Shares' : `${price(l.strike)} ${l.right === 'C' ? 'Call' : 'Put'}`}</td>
                  <td style={{ ...s.td, padding: '4px 6px', fontSize: '11px', color: 'var(--text-muted)' }}>{v ? `${price(v.bid)} / ${price(v.ask)}` : l.kind === 'stk' ? 'share' : '—'}</td>
                  <td style={{ ...s.td, padding: '4px 6px', width: '76px' }}>
                    <input style={small} type="number" step="any" value={l.price} onChange={e => edit(l.key, { price: Number(e.target.value) || 0 })} />
                  </td>
                  <td style={{ ...s.td, padding: '4px 6px' }}><span style={{ ...s.link, color: 'var(--text-muted)' }} onClick={() => remove(l.key)}>✕</span></td>
                </tr>
              );
            })}</tbody>
          </table>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '10px' }}>
            <div>
              <div style={s.label}>Strategies (units)</div>
              <input style={s.input} type="number" min="1" step="1" value={n} onChange={e => { setN(e.target.value); reset(); }} />
            </div>
            <div>
              <div style={s.label}>Order type</div>
              <select style={{ ...s.select, width: '100%' }} value={type} onChange={e => { setType(e.target.value); reset(); }}>
                <option value="LIMIT">Limit (net price)</option><option value="MARKET">Market</option>
              </select>
            </div>
            {opt.length > 0 && type === 'LIMIT' && (
              <div>
                <div style={s.label}>Net {netPx >= 0 ? 'debit' : 'credit'} per unit</div>
                <input style={s.input} type="number" step="0.01" value={net === '' ? Number(legNet.toFixed(4)) : net} onChange={e => { setNet(e.target.value); reset(); }} />
                <div style={{ display: 'flex', gap: '8px', marginTop: '4px', fontSize: '11px' }}>
                  <span style={s.link} onClick={() => { setNet(''); reset(); }}>Leg prices</span>
                  <span style={s.link} onClick={() => { setNet(String(Number(natural.toFixed(2)))); reset(); }}>Natural</span>
                </div>
              </div>
            )}
            <div>
              <div style={s.label}>Time in force</div>
              <select style={{ ...s.select, width: '100%' }} value={tif} onChange={e => { setTif(e.target.value); reset(); }}>
                <option value="DAY">Day</option><option value="GTC">Good till cancelled</option>
              </select>
            </div>
          </div>

          {a && (
            <div style={{ padding: '10px 12px', background: 'var(--bg)', borderRadius: 'var(--radius)', marginBottom: '10px' }}>
              {kv(a.cost >= 0 ? 'Cost (debit)' : 'Credit received', `${num(Math.abs(a.cost))} ${ccy}`)}
              {kv('Max profit', a.upUnlimited ? 'Unlimited' : `${num(a.maxProfit)} ${ccy}`, 'var(--success)')}
              {kv('Max loss', a.downUnlimited ? 'Unlimited' : `${num(a.maxLoss)} ${ccy}`, 'var(--error)')}
              {kv('Breakeven' + (a.breakevens.length > 1 ? 's' : ''), a.breakevens.length ? a.breakevens.map(b => price(b)).join(' · ') : '—')}
              {kv('Chance of profit ≈', isNum(a.pop) ? `${Math.round(a.pop * 100)}%` : '—')}
              {kv('Net delta (shares)', dec(greeks.delta, 1))}
              {kv('Net gamma', dec(greeks.gamma, 3))}
              {kv(`Net theta / day (${ccy})`, dec(greeks.theta, 2))}
              {kv(`Net vega (${ccy} per vol pt)`, dec(greeks.vega, 2))}
              <div style={{ ...s.meta, fontSize: '11px', marginTop: '4px' }}>{days} days to expiry. Chance of profit uses the legs' implied volatility - a guide, not a promise.</div>
            </div>
          )}
          {a && <Payoff pnl={a.pnl} lo={lo} hi={hi} und={und} ccy={ccy} breakevens={a.breakevens} />}

          <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
            <button style={{ ...s.btn, flex: 1 }} onClick={doPreview} disabled={!ready || !opt.length}>{busy === 'preview' ? 'Checking…' : 'Preview'}</button>
            <button onClick={place} disabled={!ready}
                    style={{ ...s.btn, flex: 2, border: 'none', fontSize: '13px', fontWeight: 600,
                             background: ready ? 'var(--accent)' : 'var(--border)', color: ready ? 'var(--on-accent)' : 'var(--text-muted)', cursor: ready ? 'pointer' : 'default' }}>
              {busy === 'place' ? 'Placing…' : `Place ${opt.length >= 2 ? 'combo' : 'order'}${units > 1 ? ` × ${units}` : ''}`}
            </button>
          </div>
          {stk && <div style={{ ...s.meta, marginTop: '6px' }}>The share leg goes to IBKR as its own limit order at the price above; the options go {opt.length >= 2 ? 'as one combo' : 'as a single order'}.</div>}
          {msg && <div style={s.ok}>{msg}</div>}
          {err && <div style={s.err}>✕ {err}</div>}
          {preview && (
            <div style={{ marginTop: '10px', padding: '10px 12px', background: 'var(--bg)', borderRadius: 'var(--radius)' }}>
              <div style={{ ...s.title, fontSize: '12px', marginBottom: '6px' }}>Preview (option legs) - nothing placed</div>
              {kv('Commission', isNum(preview.commission) ? `${num(preview.commission)} ${preview.commissionCurrency || ''}`
                : isNum(preview.minCommission) ? `${num(preview.minCommission)} – ${num(preview.maxCommission)} ${preview.commissionCurrency || ''}` : '—')}
              {kv('Initial margin change', num(preview.initMarginChange))}
              {kv('Maintenance margin change', num(preview.maintMarginChange))}
              {kv('Equity with loan after', num(preview.equityWithLoanAfter))}
              {preview.warningText && <div style={{ ...s.err, fontSize: '12px' }}>⚠ {preview.warningText}</div>}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ── Chain table ──────────────────────────────────────────────────────────────
function ChainTable({ chain, isPicked, onPick }) {
  const und = chain.underlying_price;
  const callCols = [['open_interest', 'OI'], ['volume', 'Vol'], ['iv', 'IV'], ['theta', 'Θ'], ['gamma', 'Γ'], ['delta', 'Δ'], ['last', 'Last'], ['bid', 'Bid'], ['ask', 'Ask']];
  const putCols  = [['bid', 'Bid'], ['ask', 'Ask'], ['last', 'Last'], ['delta', 'Δ'], ['gamma', 'Γ'], ['theta', 'Θ'], ['iv', 'IV'], ['volume', 'Vol'], ['open_interest', 'OI']];
  const fmt = (k, v) => k === 'iv' ? pct1(v)
    : k === 'delta' ? (isNum(v) ? (Number(v) * 100).toFixed(1) : '—')
    : k === 'gamma' ? dec(v, 4)
    : k === 'theta' ? dec(v, 3)
    : ['volume', 'open_interest'].includes(k) ? qty(v) : price(v);
  const th = { ...s.th, textAlign: 'right', padding: '6px 8px' };

  const cell = (leg, right, strike, k, itm) => {
    const clickable = (k === 'bid' || k === 'ask') && leg && isNum(leg[k]) && leg[k] > 0;
    const picked = (k === 'bid' || k === 'ask') && isPicked(strike, right, k === 'ask' ? 'BUY' : 'SELL');
    return (
      <td key={k} onClick={clickable ? () => onPick({ strike, right, side: k === 'ask' ? 'BUY' : 'SELL', price: leg[k] }) : undefined}
          title={clickable ? (k === 'ask' ? 'Buy at the ask' : 'Sell at the bid') : undefined}
          style={{ ...s.td, padding: '6px 8px', textAlign: 'right', background: picked ? 'var(--accent-dim)' : itm ? 'var(--accent-faint)' : undefined,
                   cursor: clickable ? 'pointer' : 'default', fontWeight: k === 'bid' || k === 'ask' ? 500 : 400,
                   color: clickable ? (k === 'ask' ? 'var(--success)' : 'var(--error)') : ['delta', 'gamma', 'theta', 'iv'].includes(k) ? 'var(--text-muted)' : 'var(--text)' }}>
        {leg ? fmt(k, leg[k]) : ''}
      </td>
    );
  };

  const rows = [];
  let marked = false;
  chain.rows.forEach(r => {
    if (!marked && isNum(und) && r.strike >= und) {     // share price line between strikes
      marked = true;
      rows.push(
        <tr key="und"><td colSpan={callCols.length + putCols.length + 1}
          style={{ padding: '3px 8px', textAlign: 'center', fontSize: '11px', color: 'var(--accent)', background: 'var(--accent-dim)', ...s.mono }}>
          ▲ share price {price(und)} {chain.currency}</td></tr>);
    }
    rows.push(
      <tr key={r.strike}>
        {callCols.map(([k]) => cell(r.call, 'C', r.strike, k, isNum(und) && r.strike < und))}
        <td style={{ ...s.td, padding: '6px 12px', textAlign: 'center', fontWeight: 600, background: 'var(--bg)', borderLeft: '1px solid var(--border)', borderRight: '1px solid var(--border)' }}>
          {price(r.strike)}</td>
        {putCols.map(([k]) => cell(r.put, 'P', r.strike, k, isNum(und) && r.strike > und))}
      </tr>);
  });

  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={s.table}>
        <thead>
          <tr>
            <th colSpan={callCols.length} style={{ ...s.th, textAlign: 'center', color: 'var(--text)' }}>Calls</th>
            <th style={{ ...s.th, textAlign: 'center' }}></th>
            <th colSpan={putCols.length} style={{ ...s.th, textAlign: 'center', color: 'var(--text)' }}>Puts</th>
          </tr>
          <tr>
            {callCols.map(([k, l]) => <th key={k} style={th}>{l}</th>)}
            <th style={{ ...th, textAlign: 'center' }}>Strike</th>
            {putCols.map(([k, l]) => <th key={k} style={th}>{l}</th>)}
          </tr>
        </thead>
        <tbody>{rows}</tbody>
      </table>
    </div>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────
export default function TradingOptions() {
  const t = useTradingAccount();
  const [params] = useSearchParams();
  const [code, setCode]       = useState((params.get('code') || '').toUpperCase());
  const [info, setInfo]       = useState(null);     // /options/params
  const [tc, setTc]           = useState('');
  const [expiry, setExpiry]   = useState('');
  const [count, setCount]     = useState(16);
  const [chain, setChain]     = useState(null);
  const [quote, setQuote]     = useState(null);
  const [loading, setLoading] = useState(false);
  const [updated, setUpdated] = useState(null);
  const [err, setErr]         = useState('');
  const [sel, setSel]         = useState(null);
  const [mode, setMode]       = useState('single');   // 'single' ticket or 'strategy' builder
  const [legs, setLegs]       = useState([]);

  useEffect(() => { document.title = code ? `${code} options | chart.ninja` : 'Options | chart.ninja'; }, [code]);

  // New underlying: its chains, then the first trading class and nearest expiry.
  useEffect(() => {
    setInfo(null); setChain(null); setSel(null); setLegs([]); setErr(''); setTc(''); setExpiry(''); setQuote(null);
    if (!t.broker || !code) return;
    api.get(`/trading/${t.broker}/options/params`, { params: { code } })
      .then(r => { setInfo(r.data); setTc(r.data.chains[0].trading_class); setExpiry(r.data.chains[0].expirations[0]); })
      .catch(e => setErr(errText(e)));
  }, [t.broker, code]);

  const load = useCallback(async () => {
    if (!t.broker || !code || !expiry) return;
    setLoading(true);
    try {
      const [c, q] = await Promise.all([
        api.get(`/trading/${t.broker}/options/chain`, { params: { code, expiry, trading_class: tc, count } }),
        api.get(`/trading/${t.broker}/quote`, { params: { code } }).catch(() => null),
      ]);
      setChain(c.data); setQuote(q?.data.quote || null); setUpdated(new Date()); setErr('');
    } catch (e) { setErr(errText(e)); }
    finally { setLoading(false); }
  }, [t.broker, code, expiry, tc, count]);
  useEffect(() => {
    setChain(null); setSel(null);
    load();
    const i = setInterval(load, CHAIN_REFRESH_MS);
    return () => clearInterval(i);
  }, [load]);

  const ch = info?.chains.find(c => c.trading_class === tc) || info?.chains[0];
  const last = quote?.last_price, prev = quote?.prev_close_price;
  const chg = isNum(last) && isNum(prev) ? last - prev : null;
  const legLive = chain?.rows.map(r => r.call || r.put).find(Boolean)?.market_data_type;

  // A new expiry drops option legs from the old one (strategies here are single-expiry); share legs stay.
  const chainExpiry = chain?.expiry;
  useEffect(() => {
    if (chainExpiry) setLegs(ls => ls.filter(l => l.kind === 'stk' || l.expiry === chainExpiry));
  }, [chainExpiry]);

  // Chain clicks: the single ticket, or add / update a leg in the strategy.
  const pick = p => {
    if (mode === 'single') { setSel(p); return; }
    const leg = optionLeg(chain, p.strike, p.right, p.side, 1, p.price);
    if (!leg || (legs.filter(l => l.kind === 'opt').length >= 4 && !legs.some(l => l.key === leg.key))) return;   // max 4 option legs
    setLegs(ls => ls.some(l => l.key === leg.key) ? ls.map(l => l.key === leg.key ? { ...l, action: leg.action, price: leg.price } : l) : [...ls, leg]);
  };
  const isPicked = (strike, right, side) => mode === 'single'
    ? !!sel && sel.strike === strike && sel.right === right && sel.side === side
    : legs.some(l => l.kind === 'opt' && l.expiry === chain?.expiry && l.strike === strike && l.right === right && l.action === side);
  const panelOpen = mode === 'strategy' || !!sel;

  return (
    <div style={s.page}>
      <Navbar />
      <div style={s.content}>
        <h1 style={s.heading}>Trading</h1>
        <TradingTabs />
        <StatusBar t={t} />
        {t.acc && (
          <>
            <div style={{ marginBottom: '20px' }}><SymbolSearch onPick={setCode} /></div>
            {!code && <div style={{ ...s.card, ...s.meta, textAlign: 'center', padding: '40px' }}>
              Search for a US or ASX share above to see its options chain.</div>}
            {code && (
              <div style={s.card}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: '14px', flexWrap: 'wrap' }}>
                  <span style={{ fontSize: '20px', fontWeight: 600, ...s.mono }}>{code}</span>
                  {isNum(last) && <span style={{ fontSize: '20px', ...s.mono }}>{price(last)}</span>}
                  {isNum(chg) && <span style={{ ...s.mono, color: tone(chg) }}>{arrow(chg)}{signed(chg)} ({signed(chg / prev * 100)}%)</span>}
                  {info && <span style={s.meta}>{info.exchange === 'ASX' ? 'ASX options' : 'US options'} · {info.currency}{ch ? ` · ×${ch.multiplier} per contract` : ''}</span>}
                  {legLive && <span style={legLive === 'live' ? s.pill('var(--success-dim)', 'var(--success)') : s.pill('var(--bg)', 'var(--text-muted)')}>{legLive.toUpperCase()}</span>}
                  <span style={{ ...s.meta, marginLeft: 'auto' }}>{loading ? 'Loading…' : updated ? `Updated ${updated.toLocaleTimeString('en-GB')}` : ''}</span>
                  <button style={s.btn} onClick={load} disabled={loading || !expiry}>Refresh</button>
                </div>
                {err && <div style={s.err}>✕ {err}</div>}

                {info && (
                  <>
                    <div style={{ display: 'flex', gap: '12px', alignItems: 'center', margin: '14px 0 8px', flexWrap: 'wrap' }}>
                      {info.chains.length > 1 && (
                        <select style={s.select} value={tc} onChange={e => { setTc(e.target.value); setExpiry(info.chains.find(c => c.trading_class === e.target.value).expirations[0]); }}
                                title="IBKR trading class - e.g. monthly and weekly option series">
                          {info.chains.map(c => <option key={c.trading_class} value={c.trading_class}>{c.trading_class} ({c.expirations.length} expiries)</option>)}
                        </select>
                      )}
                      <span style={s.meta}>Strikes</span>
                      <select style={s.select} value={count} onChange={e => setCount(Number(e.target.value))}>
                        {STRIKE_COUNTS.map(n => <option key={n} value={n}>{n} nearest</option>)}
                      </select>
                      <div style={{ display: 'flex', marginLeft: 'auto' }}>
                        {[['single', 'Single option'], ['strategy', 'Strategy builder']].map(([m, label], i) => (
                          <button key={m} onClick={() => { setMode(m); setSel(null); }}
                                  style={{ ...s.seg(mode === m), flex: 'none', padding: '6px 14px',
                                           borderRadius: i ? '0 var(--radius) var(--radius) 0' : 'var(--radius) 0 0 var(--radius)' }}>{label}</button>
                        ))}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '6px', overflowX: 'auto', paddingBottom: '6px' }}>
                      {ch?.expirations.map(e => (
                        <button key={e} onClick={() => setExpiry(e)}
                                style={{ ...s.btn, flexShrink: 0, padding: '5px 10px', textAlign: 'center', lineHeight: 1.3,
                                         ...(e === expiry ? { background: 'var(--accent)', color: 'var(--on-accent)', border: '1px solid var(--accent)' } : {}) }}>
                          <div>{expLabel(e)}</div>
                          <div style={{ fontSize: '10px', opacity: 0.8 }}>{dte(e)}d</div>
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}

            {code && (chain || (loading && expiry)) && (
              <div style={{ display: 'grid', gridTemplateColumns: panelOpen ? 'minmax(0, 1fr) 400px' : 'minmax(0, 1fr)', gap: '20px', alignItems: 'start' }}>
                <div style={s.card}>
                  {!chain && <div style={{ ...s.meta, padding: '30px 0', textAlign: 'center' }}>Loading the chain - collecting quotes and greeks for each option…</div>}
                  {chain && (
                    <>
                      <div style={{ ...s.meta, marginBottom: '8px' }}>
                        {expLabel(chain.expiry)} · {dte(chain.expiry)} days to expiry · {chain.trading_class}
                      </div>
                      <div style={{ ...s.meta, marginBottom: '6px' }}>
                        {mode === 'single' ? 'Click a bid to sell or an ask to buy that option.' : 'Click bids (sell) and asks (buy) to add legs to the strategy.'} Shaded = in the money.
                      </div>
                      <ChainTable chain={chain} isPicked={isPicked} onPick={pick} />
                    </>
                  )}
                </div>
                {mode === 'single' && sel && chain && <OptionTicket broker={t.broker} acc={t.acc} chain={chain} sel={sel} onClose={() => setSel(null)} />}
                {mode === 'strategy' && chain && <StrategyPanel broker={t.broker} acc={t.acc} chain={chain} legs={legs} setLegs={setLegs} />}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
